import { ItemMetadata } from 'src/API';

export enum Attribute_Type {
  STANDARD = 'STANDARD',
  PRODUCT_LINE = 'PRODUCT LINE',
  PROGRAM = 'PROGRAM',
}

export enum ChangeReviewStatus {
  APPROVED = 'APPROVED',
  DENIED = 'DENIED',
  PENDING = 'PENDING',
}

export enum AdminActionStatus {
  PENDING_APPROVAL = 'Pending',
  NO_ACTION = 'No Action',
}

// Response of listAttributeDetails Query
export type AttributeDetailsResponse = {
  data: Array<AttributeDetails>;
  pagination: PaginationMetadata;
};

export type PaginationMetadata = {
  current_page: number;
  has_next: boolean;
  has_previous: boolean;
  page_size: number;
  total_count: number;
  total_pages: number;
};

export interface AttributeDetails {
  attribute_type_id: number;

  attribute_type_label: string; // Example: CATEGORY | CHANNEL | INDOOR/OUTDOOR | PC/PK COUNT
  attribute_level_label: Attribute_Type; // STANDARD | PROGRAM | PRODUCT LINE |

  attribute_type_justification?: string | null;
  attribute_type_status: ChangeReviewStatus;

  attribute_value_details: Array<AttributeValueDetails>;
  item_metadata: ItemMetadata;
}

export interface AttributeValueDetails {
  attribute_value_id: number;
  attribute_value_label: string;

  attribute_value_justification?: string | null;
  attribute_value_status: ChangeReviewStatus;

  product_line_count: number;
  rpn_count: number;
  fpn_count: number;
  item_metadata: ItemMetadata;
}

// ------------------------------------------------------------

export interface ManageAttributesTableItems extends AttributeDetails {
  admin_action_status: AdminActionStatus;
  attribute_value_labels: string;
  rpn_count: number;
  fpn_count: number;
  product_line_count: number;
}

export type AttributeDetailsInput = {
  attribute_level_label: string;
  attribute_type_id?: number | null;
  attribute_type_justification?: string | null;
  attribute_type_label: string;
  attribute_type_status: ChangeReviewStatus;
  attribute_value_details: Array<AttributeValueDetailsInput>;
  item_metadata: ItemMetadata;
};

export type AttributeValueDetailsInput = {
  attribute_value_id?: number | null;
  attribute_value_justification?: string | null;
  attribute_value_label: string;
  attribute_value_status: ChangeReviewStatus;
  item_metadata: ItemMetadata;
};

export interface AttributeState {
  masterAttributesData: AttributeDetails[];
  selectedAttribute: ManageAttributesTableItems | null;
  isLoading: boolean;
  error: string | null;
  hasAttemptedFetch: boolean;
  hasSuccessfulFetch: boolean;
  lastFetchTimestamp: number | null;
}

export interface ExistingAttributesTableItems {
  attributeLevelLabel: string;
  attributeTypeId: number;
  attributeTypeLabel: string;
  attributeTypeStatus: ChangeReviewStatus;
  attributeTypeJustification: string | null;
  attributeTypeItemMetadata: ItemMetadata;
  attributeValueId: number;
  attributeValueLabel: string;
  attributeValueStatus: ChangeReviewStatus;
  attributeValueJustification: string | null;
  rpn_count: number;
  fpn_count: number;
  productLines: number[];
  fpnCount: number;
  rpnCount: number;
  productLineCount: number;
  attributeValueItemMetadata?: ItemMetadata;
  requestedBy: string;
  requestedAt: string;
  reviewedBy: string;
  reviewedAt: string;
}

export interface PendingAttributesTableItems {
  attributeLevelLabel: string;
  attributeTypeId: number;
  attributeTypeLabel: string;
  attributeTypeStatus: ChangeReviewStatus;
  attributeTypeJustification: string | null;
  attributeTypeItemMetadata: ItemMetadata;
  attributeValueId: number;
  attributeValueLabel: string;
  attributeValueStatus: ChangeReviewStatus;
  attributeValueJustification: string | null;
  attributeValueItemMetadata: ItemMetadata;
  requestedBy: string;
  requestedAt: string;
}

export interface AttributeValuesForm {
  attributeValueLabel: string;
  attributeValueStatus: ChangeReviewStatus;
  attributeValueJustification: string;
}

export interface CreateAttributeFormValues {
  attributeLevelLabel: string | null;
  attributeTypeId?: number | null;
  attributeTypeLabel: string | null;
  attributeTypeStatus: ChangeReviewStatus;
  attributeTypeJustification: string | null;
  attributeTypeItemMetadata?: ItemMetadata | null;
  attributeValues: AttributeValuesForm[];
}

export interface CreateAttributeFormState {
  values: CreateAttributeFormValues;
  dirty: boolean;
  isValid: boolean;
  isSubmitting: boolean;
  isValidating: boolean;
}

// Ticketing types for attribute approval ticket creation
export interface CreateAttributeApprovalTicketInput {
  attribute_type: string;
  attribute_values: string[];
  attribute_level: string;
  user_alias: string;
  justifications: string[];
}

export interface TicketCreationResponse {
  ticket_id: string;
}
